<!-- Floating Chat Wrapper -->
<div [attr.dir]="currentDir" class="chat-wrapper">

  <!-- Floating Chat Button -->
  <button type="button" class="chat-fab" (click)="toggleChat()" [@fabRotate]="isOpen ? 'open' : 'closed'"
    [attr.aria-label]="isOpen ? 'Close chat' : 'Open chat'">
    <img *ngIf="!isOpen" src="assets/img/robot.png" alt="Chat Bot" class="chat-gif" />
    <i *ngIf="isOpen" class="bi bi-plus-lg"></i>
  </button>

  <!-- Chat Window -->
  <div class="chat-window" [@slideInOut]="isOpen ? 'in' : 'out'" [class.show]="isOpen">

    <!-- Chat Header -->
    <div class="chat-header">
      <div class="header-left">
        <div class="ai-avatar">
          <img src="assets/img/robot.png" alt="AI Avatar" class="ai-text">
          <span class="online-dot"></span>
        </div>
        <div class="chat-title">
          <h6>{{ selectedApplicationCode === 'NMS' ? 'Saba' : ('CHAT_ASSIST' | translate) }}</h6>
          <small class="chat-status">{{ "CHAT_STATUS" | translate }}</small>
        </div>
      </div>
      <button type="button" class="close-btn" (click)="closeChat()">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="application-selector" *ngIf="selectedApplicationCode === 'NMS'">
      <label for="hermes-authority-mode">Hermes mode</label>
      <select id="hermes-authority-mode" [(ngModel)]="selectedHermesMode" [disabled]="isSending || isTyping">
        <option value="OBSERVER">Observer</option><option value="ANALYST">Analyst</option>
        <option value="ADVISOR">Advisor</option><option value="OPERATOR">Operator</option>
        <option value="AUTONOMOUS">Autonomous</option>
      </select>
    </div>

    <div class="application-selector" *ngIf="enabledApplications.length">
      <label for="internal-chat-application">Application</label>
      <select id="internal-chat-application" [ngModel]="selectedApplicationCode"
        (ngModelChange)="onApplicationChange($event)" [disabled]="isSending || isTyping">
        <option *ngFor="let application of enabledApplications" [value]="application.applicationCode">
          {{ application.applicationName }}
        </option>
      </select>
    </div>

    <!-- Chat Messages -->
    <div class="chat-messages" #messagesContainer>
      <div *ngFor="let message of messages" class="message" [ngClass]="{
          'user-message': message.sender === 'user',
          'bot-message': message.sender === 'bot'
        }">
        <div class="message-content">

          <!-- Plain Text or Streaming Message -->
          <div *ngIf="!message.tableData && message.text" class="message-bubble">
            <span *ngIf="!message.isLoading">{{ message.text | translateOnce }}</span>

            <!-- Loading indicator with animated dots -->
            <div *ngIf="message.isLoading" class="loading-container">
              <span>{{ message.text | translateOnce }}</span>
              <div class="loading-dots">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          </div>

          <!-- HTML Content -->
          <div *ngIf="message.html" class="message-bubble html-bubble" [innerHTML]="message.html"></div>

          <!-- Options Selection (Buttons) -->
          <div *ngIf="message.showOptions && message.options" class="action-buttons">
            <button *ngFor="let option of message.options" type="button" class="action-btn"
              (click)="onOptionSelect(message.currentField!, option)">
              {{ option.text | translate }}
            </button>
          </div>

          <!-- Dropdown Selection (for optional fields with many options) -->
          <div *ngIf="message.showDropdown && message.dropdownOptions" class="mt-2 custom-dropdown-container">
            <div class="custom-dropdown" [class.open]="dropdownOpen === message.currentField">
              <button type="button" class="custom-dropdown-toggle" (click)="toggleDropdown(message.currentField!)">
                {{ (getSelectedOptionText(message.currentField!) || 'CHATBOT_SELECT_OPTION') | translate }}
                <i class="bi bi-chevron-down ms-auto"></i>
              </button>
              <div class="custom-dropdown-menu" *ngIf="dropdownOpen === message.currentField">
                <div class="dropdown-search-container">
                  <input type="text" class="form-control form-control-sm dropdown-search-input"
                    [placeholder]="'CHATBOT_SEARCH_PLACEHOLDER' | translate" [(ngModel)]="dropdownSearchQuery"
                    (click)="$event.stopPropagation()">
                </div>
                <div class="custom-dropdown-item" *ngFor="let option of getFilteredOptions(message.dropdownOptions!)"
                  (click)="selectDropdownOption(message.currentField!, option)">
                  {{ option.text | translate }}
                </div>
                <div *ngIf="getFilteredOptions(message.dropdownOptions!).length === 0" class="no-results-msg">
                  {{ 'CHATBOT_NO_RESULTS' | translate }}
                </div>
              </div>
            </div>
            <button *ngIf="message.allowSkip" type="button" class="btn btn-sm btn-secondary w-100 mt-2"
              (click)="onSkipDropdownField(message.currentField!)">
              {{ 'CHATBOT_SKIP' | translate }}
            </button>
          </div>

          <!-- Table Format -->
          <div *ngIf="message.tableData" class="message-bubble html-bubble">
            <div class="table-responsive">
              <table class="table table-bordered table-sm mb-0">
                <thead class="table-light">
                  <tr>
                    <th *ngFor="let key of message.tableData[0] | keyvalue">{{ key.key }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of message.tableData">
                    <td *ngFor="let key of row | keyvalue">{{ key.value }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- Action Buttons -->
          <div *ngIf="message.showButtons && message.buttons" class="action-buttons">
            <button *ngFor="let button of message.buttons" type="button" class="action-btn"
              (click)="onButtonClick(button.action, message)">
              {{ button.text | translate }}
            </button>
          </div>

          <!-- Timestamp -->
          <div class="message-time">
            {{ message.timestamp | date: 'shortTime' }}
          </div>
        </div>
      </div>

      <!-- Typing Indicator -->
      <div id="chatbot-typing-indicator" class="message bot-message" *ngIf="isTyping">
        <div class="message-content">
          <div class="message-bubble typing-indicator">
            <span class="emoji">🤔</span>
            <div class="typing-indicator">
              Thinking
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Document Upload Section -->
    <div *ngIf="showDocumentUpload" class="document-upload-container">
      <!-- Show uploaded documents list -->
      <div *ngIf="documentList.length > 0" class="mb-3">
        <label class="form-label small fw-bold">{{ "ATTACHMENTS" | translate }}
          ({{documentList.length}}):</label>
        <ul class="list-group list-group-sm">
          <li *ngFor="let doc of documentList; let i = index"
            class="list-group-item d-flex justify-content-between align-items-center py-1 px-2 small">
            <span>
              <i class="bi bi-file-earmark-check text-success me-1"></i>
              {{doc.documentName || 'Document ' + (i + 1)}}
            </span>
            <span class="badge bg-success rounded-pill">✓</span>
          </li>
        </ul>
      </div>

      <div class="mb-2">
        <label class="form-label small">{{ "CHATBOT_SELECT_DOCUMENT" | translate }}</label>
        <input type="file" class="form-control form-control-sm" (change)="onDocumentSelect($event)"
          accept=".png,.jpg,.jpeg,.gif,.pdf">
      </div>
      <div *ngIf="fileError" class="alert alert-danger alert-sm py-1">
        {{ "CHATBOT_INVALID_FILE" | translate }}
      </div>
      <button type="button" class="btn btn-sm"
        [class.btn-success]="documentList.length > 0"
        [class.btn-secondary]="documentList.length === 0"
        (click)="skipDocumentUpload()">
        {{ (documentList.length > 0 ? 'CHATBOT_DONE_UPLOAD' : 'CHATBOT_SKIP_DOCUMENT') | translate }}
      </button>
    </div>

    <!-- Submit Button Section -->
    <div *ngIf="showSubmitButton" class="submit-container">
      <button type="button" class="btn btn-success btn-sm px-4" (click)="onSubmitComplaint()">
        <i class="bi bi-check-circle me-1"></i>
        {{ "CHATBOT_SUBMIT_COMPLAINT" | translate }}
      </button>
    </div>

    <!-- Chat Input Section with Suggestions -->
    <div class="chat-footer-wrapper" [class.d-none]="showDocumentUpload || showSubmitButton">
      <!-- Input Area -->
      <div class="chat-input-container">
        <textarea [(ngModel)]="currentMessage" (keydown)="onKeyPress($event)" (input)="onInput($event)"
          [placeholder]="getInputPlaceholder() | translate" rows="1" class="chat-textarea"
          [disabled]="isInputDisabled()">
      </textarea>
        <!-- <app-voice-input #voiceInputRef [disabled]="isInputDisabled()" [existingText]="currentMessage"
          (textReceived)="onVoiceText($event)" (interimText)="onInterimVoiceText($event)">
        </app-voice-input> -->
        <button type="button" class="send-btn" (click)="sendMessage()" aria-label="Send message"
          [disabled]="isSendButtonDisabled()">
          <i class="bi bi-send-fill"></i>
        </button>
      </div>

      <!-- Character Counter -->
      <div *ngIf="getCurrentMaxLength() as maxLength" class="char-counter"
        [class.text-danger]="(currentMessage.length || 0) > maxLength">
        {{ currentMessage.length || 0 }} / {{ maxLength }}
      </div>

    </div>
  </div>

  <!-- Backdrop -->
  <div class="chat-backdrop" [class.show]="isOpen" (click)="closeChat()"></div>
</div>
